/**
 * Flow map icons: a small inline-SVG set drawn like lucide (24x24 grid,
 * 2px round strokes, `currentColor`). Path data is copied from lucide
 * (ISC licence). `@wa/widgets` cannot depend on lucide-react, and inline SVG
 * renders the same on the server, in the RSC template preview and in the
 * browser.
 *
 * No handlers, no state: safe inside the static picture and the server table.
 */
import type { ReactElement, ReactNode } from "react";
import type { FlowNodeKind } from "./types.js";

export interface FlowIconProps {
  /** Pixel size of the square icon. Default 16. */
  size?: number;
  className?: string;
}

function Svg({ size = 16, className, children }: FlowIconProps & { children: ReactNode }): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

/** lucide "workflow": the canvas view. */
export function IconCanvas(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect width="8" height="8" x="3" y="3" rx="2" />
      <path d="M7 11v4a2 2 0 0 0 2 2h4" />
      <rect width="8" height="8" x="13" y="13" rx="2" />
    </Svg>
  );
}

/** lucide "table-2": the table view. */
export function IconTable(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
    </Svg>
  );
}

export function IconPlus(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </Svg>
  );
}

/** lucide "trash-2". */
export function IconTrash(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </Svg>
  );
}

/** lucide "maximize": fit the map into view. */
export function IconFit(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </Svg>
  );
}

export function IconZoomIn(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="11" x2="11" y1="8" y2="14" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </Svg>
  );
}

export function IconZoomOut(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" x2="16.65" y1="21" y2="16.65" />
      <line x1="8" x2="14" y1="11" y2="11" />
    </Svg>
  );
}

/** lucide "undo-2". */
export function IconUndo(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
    </Svg>
  );
}

/** lucide "redo-2". */
export function IconRedo(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13" />
    </Svg>
  );
}

/** lucide "network": auto layout. */
export function IconLayout(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect x="16" y="16" width="6" height="6" rx="1" />
      <rect x="2" y="16" width="6" height="6" rx="1" />
      <rect x="9" y="2" width="6" height="6" rx="1" />
      <path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />
      <path d="M12 12V8" />
    </Svg>
  );
}

export function IconArrowDown(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </Svg>
  );
}

export function IconArrowRight(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </Svg>
  );
}

export function IconX(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </Svg>
  );
}

export function IconLink(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Svg>
  );
}

/** lucide "external-link". */
export function IconExternal(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </Svg>
  );
}

export function IconCheck(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M20 6 9 17l-5-5" />
    </Svg>
  );
}

export function IconChevronUp(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m18 15-6-6-6 6" />
    </Svg>
  );
}

export function IconChevronDown(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m6 9 6 6 6-6" />
    </Svg>
  );
}

export function IconChevronsUpDown(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="m7 15 5 5 5-5" />
      <path d="m7 9 5-5 5 5" />
    </Svg>
  );
}

/** lucide "locate": centre the map on a node. */
export function IconCrosshair(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <line x1="2" x2="5" y1="12" y2="12" />
      <line x1="19" x2="22" y1="12" y2="12" />
      <line x1="12" x2="12" y1="2" y2="5" />
      <line x1="12" x2="12" y1="19" y2="22" />
      <circle cx="12" cy="12" r="7" />
    </Svg>
  );
}

/** lucide "circle-play": a start node. */
function IconStart(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M9 9.003a1 1 0 0 1 1.517-.859l4.997 2.997a1 1 0 0 1 0 1.718l-4.997 2.997A1 1 0 0 1 9 14.996z" />
      <circle cx="12" cy="12" r="10" />
    </Svg>
  );
}

/** lucide "square": a step node. */
function IconStep(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <rect width="18" height="18" x="3" y="3" rx="2" />
    </Svg>
  );
}

/** lucide "diamond": a decision node. */
function IconDecision(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z" />
    </Svg>
  );
}

/** lucide "flag": an end node. */
function IconEnd(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M4 22V4a1 1 0 0 1 .4-.8A6 6 0 0 1 8 2c3 0 5 2 7.333 2q2 0 3.067-.8A1 1 0 0 1 20 4v10a1 1 0 0 1-.4.8A6 6 0 0 1 16 16c-3 0-5-2-8-2a6 6 0 0 0-4 1.528" />
    </Svg>
  );
}

/** lucide "sticky-note": a note. */
function IconNote(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <path d="M21 9a2.4 2.4 0 0 0-.706-1.706l-3.588-3.588A2.4 2.4 0 0 0 15 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2z" />
      <path d="M15 3v5a1 1 0 0 0 1 1h5" />
    </Svg>
  );
}

/** lucide "frame": a group. */
function IconGroup(p: FlowIconProps): ReactElement {
  return (
    <Svg {...p}>
      <line x1="22" x2="2" y1="6" y2="6" />
      <line x1="22" x2="2" y1="18" y2="18" />
      <line x1="6" x2="6" y1="2" y2="22" />
      <line x1="18" x2="18" y1="2" y2="22" />
    </Svg>
  );
}

const KIND_ICON: Readonly<Record<FlowNodeKind, (p: FlowIconProps) => ReactElement>> = {
  start: IconStart,
  step: IconStep,
  decision: IconDecision,
  end: IconEnd,
  note: IconNote,
  group: IconGroup,
};

/** The icon of a node kind. An unknown kind draws the plain step square. */
export function KindIcon({ kind, ...rest }: FlowIconProps & { kind: FlowNodeKind }): ReactElement {
  const Icon = KIND_ICON[kind] ?? IconStep;
  return <Icon {...rest} />;
}
